import React, { useEffect, useMemo, useState } from "react";
import useModal, { MODAL_LG } from "../../../../theme/Component/hooks/useModal";
import { Modal } from "../../../../theme/Component/Modal";
import { toast } from "react-toastify";
import { BasicButton } from "../../../../theme/Component/BasicButton";

// Helpers
function stripDataUrlPrefix(base64) {
    // Soporta base64 puro o "data:application/pdf;base64,...."
    const idx = base64.indexOf("base64,");
    return idx !== -1 ? base64.slice(idx + "base64,".length) : base64;
}

function base64ToBlob(base64, mimeType) {
    const clean = stripDataUrlPrefix(base64);
    const byteChars = atob(clean);
    const byteNumbers = new Array(byteChars.length);

    for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
    }

    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

function isImage(mime) {
    return typeof mime === "string" && mime.startsWith("image/");
}

function isPdf(mime) {
    return mime === "application/pdf";
}

/**
 * Props esperadas:
 * - modalData: { base64: string, mimeType: string, filename?: string } | null
 */
export const VisorDocumentBase64 = ({ modalData, idDocument }) => {
    const { modal, setModal, close, open } = useModal("Documento", MODAL_LG);

    const [objectUrl, setObjectUrl] = useState(null);
    const [blob, setBlob] = useState(null);

    const mimeType = modalData?.mimeType || "";
    const filename = modalData?.filename || "documento";

    const viewType = useMemo(() => {
        if (!mimeType) return "unknown";
        if (isPdf(mimeType)) return "pdf";
        if (isImage(mimeType)) return "image";
        return "download";
    }, [mimeType]);

    useEffect(() => {
        if (!idDocument) return;
        if (!modalData?.base64 || !modalData?.mimeType) {
            toast.error("Error al cargar el documento")
            return;
        }

        try {
            const b = base64ToBlob(modalData.base64, modalData.mimeType);
            const url = URL.createObjectURL(b);

            setBlob(b);
            setObjectUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });

            open();
        } catch (e) {
            console.error("Error creando Blob/URL desde Base64:", e);
            open();
        }
        return () => {
            setObjectUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            setBlob(null);
        };
    }, [idDocument]);

    const handleDownload = () => {
        if (!blob) return;

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal modal={modal} updateModal={setModal}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {filename}
                    </div>
                </div>

                <div style={{ width: "100%", height: "70vh", border: "1px solid #e5e5e5", borderRadius: 8, overflow: "hidden" }}>
                    {!modalData?.base64 || !modalData?.mimeType ? (
                        <div style={{ padding: 16 }}>No hay documento para mostrar.</div>
                    ) : !objectUrl ? (
                        <div style={{ padding: 16 }}>Preparando documento…</div>
                    ) : viewType === "pdf" ? (
                        <iframe
                            title="PDF Viewer"
                            src={objectUrl}
                            style={{ width: "100%", height: "100%", border: 0 }}
                        />
                    ) : viewType === "image" ? (
                        <img
                            alt={filename}
                            src={objectUrl}
                            style={{ width: "100%", height: "100%", objectFit: "contain", background: "#fff" }}
                        />
                    ) : (
                        <div style={{ padding: 16 }}>
                            <p style={{ marginTop: 0 }}>
                                Este tipo de archivo no se puede previsualizar.
                            </p>
                            <BasicButton useMargin children={`Descargar ${filename}`} eventClick={handleDownload} icon="ri-file-download-line" />
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
